import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { X, CreditCard, Banknote, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import { functions } from '../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { formatCurrency, getPaymentSyncWarning, formatLocalDate } from '../../utils/formatters'; // localstamp-b3f96d15 — LOCAL calendar day for payment dates (was UTC via toISOString)
import { computeInvoiceBalance } from '../../utils/invoiceBalance'; // s0paycap-2e9d5b71 — same balance the server caps at

const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// staffpay-6a2d9e47 — duplicate-protection key for a staff manual payment. Must match the
// server's shape (functions/utils/staffPayment.js isValidRequestId: [A-Za-z0-9_-]{8,128}).
const newPaymentRequestId = () => {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    } catch (e) { /* fall through */ }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

// --- CHECKOUT FORM (UNCHANGED) ---
const CheckoutForm = ({ invoice, onSuccess, onError, syncWarning, connectedAccountId }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);

    // s0bcharge-bc5e9925 — S0b: the same balance the server charges (computeInvoiceBalance twin).
    // Was `balanceDue > 0 ? balanceDue : total`, which showed (and, for staff, sent) the full
    // total when the saved balance was 0 or missing. The server now ignores this figure for every
    // card payment; it is only the label on the Pay button.
    const amountToPay = computeInvoiceBalance(invoice).balanceDue;

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!stripe || !elements) return;

        setIsProcessing(true);

        // pitrace-9d4b2e18: also capture paymentIntent -- it carries .id, needed
        // for later dedup/audit. Present on inline completion (redirect:"if_required");
        // a redirect path never returns here, so we never record without it.
        const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                // Redirect methods land on a real, public status page (not the
                // old non-existent /payment-success that fell through to the
                // staff CRM). acct scopes the retrieve to the connected account
                // this direct-charge PI lives on. Stripe appends
                // payment_intent_client_secret / redirect_status on return.
                return_url: `${window.location.origin}/portal/payment-result?acct=${connectedAccountId}`,
            },
            redirect: "if_required"
        });

        if (error) {
            onError(error.message);
            setIsProcessing(false);
        } else {
            try {
                const recordPayment = httpsCallable(functions, 'createQuickBooksPayment');
                await recordPayment({
                    invoiceId: invoice.id,
                    paymentAmount: amountToPay,
                    paymentDate: formatLocalDate(),
                    paymentMethod: "CreditCard",
                    reference: "Stripe Online Payment",
                    qbCustomerId: invoice.customerDetails?.qbId,
                    qbInvoiceId: invoice.qbInvoiceId,
                    stripePaymentIntentId: paymentIntent?.id
                });
                onSuccess();
            } catch (qbError) {
                console.error("Payment succeeded but QB Sync failed:", qbError);
                onSuccess();
            }
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <PaymentElement />
            {syncWarning && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                    {/* qbpaysync-e15a8c60 */}
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                    <span>{syncWarning}</span>
                </div>
            )}
            <button
                disabled={!stripe || isProcessing}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50 flex justify-center items-center gap-2"
            >
                {isProcessing ? <Loader2 className="animate-spin" /> : `Pay ${formatCurrency(amountToPay)}`}
            </button>
        </form>
    );
};

// --- MANUAL FORM (UNCHANGED) ---
const ManualPaymentForm = ({ invoice, onSuccess, syncWarning }) => {
    const [amount, setAmount] = useState('');
    const [method, setMethod] = useState('Cheque');
    const [date, setDate] = useState(formatLocalDate());
    const [reference, setReference] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState(false);
    const [syncFailed, setSyncFailed] = useState(null); // qbpaysync-8e5b1a02

    useEffect(() => {
        const balance = (invoice.total || 0) - (invoice.amountPaid || 0);
        setAmount(balance > 0 ? balance.toFixed(2) : "0.00");
    }, [invoice]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setFormError('');
        setFormSuccess(false);

        // s0paycap-2e9d5b71 — S0 (founder decision, Marcus 2026-10-14): a payment recorded against
        // THIS invoice can be at most its balance due. This REPLACES Seam 1's warn-and-allow
        // confirm (record-it-anyway): the server (createQuickBooksPayment) now refuses an
        // overshoot, so offering it here would only lead to an error. Any extra money goes to
        // the customer account (Payment History tab → Record Payment), which can still hold a
        // credit balance. Same balance math as the server (computeInvoiceBalance twin), compared
        // in whole cents like staffPayment.exceedsBalance.
        const _entered = parseFloat(String(amount).replace(/[^0-9.-]+/g, '')) || 0;
        const _remaining = computeInvoiceBalance(invoice).balanceDue;
        if (_remaining <= 0.01) {
            const _when = invoice.settledViaStatus === true && invoice.settledAt
                ? ` (it was marked paid on ${new Date(invoice.settledAt).toLocaleDateString()})` : '';
            setFormError(`This invoice has nothing left to pay${_when}, so no payment can be recorded on it. To take money from this customer, use Record Payment on their Payment History tab, which adds it to their account.`);
            setIsSubmitting(false);
            return;
        }
        if (Math.round(_entered * 100) > Math.round(_remaining * 100)) {
            setFormError(`This invoice only has ${formatCurrency(_remaining)} left to pay. Record ${formatCurrency(_remaining)} or less here. For any extra, use Record Payment on the customer's Payment History tab, which adds it to their account as a credit.`);
            setIsSubmitting(false);
            return;
        }

        try {
            const recordPayment = httpsCallable(functions, 'createQuickBooksPayment');
            // staffpay-6a2d9e47 — one requestId per submit: the server records a given id at most
            // once (and never posts it to QuickBooks twice). QuickBooks ids are no longer sent —
            // the server reads them from the invoice and ignored any client copy.
            const _res = await recordPayment({
                invoiceId: invoice.id,
                paymentAmount: amount,
                paymentDate: date,
                paymentMethod: method,
                reference: reference,
                requestId: newPaymentRequestId(),
            });
            const _syncErr = (_res && _res.data && _res.data.syncError) || null; // qbpaysync-c07d9f16
            setSyncFailed(_syncErr);
            setFormSuccess(true);
            setTimeout(() => { onSuccess(); }, _syncErr ? 4000 : 1500);
        } catch (error) {
            console.error(error);
            setFormError(error.message || 'Failed to record payment. Please try again.');
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700">Amount Received</label>
                <div className="relative mt-1 rounded-md shadow-sm">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                        type="number"
                        step="0.01"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="block w-full rounded-md border-gray-300 pl-7 py-2 border focus:ring-blue-500 focus:border-blue-500"
                        required
                    />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Payment Date</label>
                    <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 border py-2 px-3 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        required
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Method</label>
                    <select
                        value={method}
                        onChange={(e) => setMethod(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 border py-2 px-3 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                        <option value="Cheque">Cheque</option>
                        <option value="Check">Check (US)</option>
                        <option value="Cash">Cash</option>
                        <option value="CreditCard">External Terminal</option>
                        <option value="DirectDebit">E-Transfer / Wire</option>
                    </select>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700">Reference / Cheque #</label>
                <input
                    type="text"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder="e.g. Chq #4021"
                    className="mt-1 block w-full rounded-md border-gray-300 border py-2 px-3 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
            </div>

            {syncWarning && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                    {/* qbpaysync-3f9c04d8 */}
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                    <span>{syncWarning}</span>
                </div>
            )}

            <button
                type="submit"
                disabled={isSubmitting || formSuccess}
                className="w-full bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700 disabled:opacity-50 flex justify-center items-center gap-2"
            >
                {isSubmitting ? <Loader2 className="animate-spin" /> : "Record Payment"}
            </button>

            {formSuccess && !syncFailed && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                    <CheckCircle size={16} className="shrink-0" />
                    Payment recorded successfully!
                </div>
            )}

            {formSuccess && syncFailed && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                    {/* qbpaysync-2a6f83be */}
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                    <span>Payment recorded in Spatchy, but syncing it to QuickBooks failed: {syncFailed}. Please add this payment in QuickBooks and verify.</span>
                </div>
            )}

            {formError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    <AlertTriangle size={16} className="shrink-0" />
                    {formError}
                </div>
            )}
        </form>
    );
};

// --- MAIN MODAL ---
// payhide-7e4c1b05 — onlinePayAvailable: staff only. false hides the "Pay Online" tab (and the
// tab bar, since only "Record Manual" would be left). Defaults true so the portal path and any
// caller that doesn't pass it are unchanged.
const PaymentModal = ({ isOpen, onClose, invoice, currentUserProfile, portalMode = false, quickbooksConnected = false, onlinePayAvailable = true }) => {
    // ✅ FIX: clientSecret and stripePromise are set TOGETHER from the backend response.
    // This guarantees the Stripe instance is always scoped to the exact same connected
    // account that created the PaymentIntent, preventing the 403 mismatch error.
    const [paymentData, setPaymentData] = useState(null); // { clientSecret, stripePromise }
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(portalMode ? 'online' : 'manual');
    const [onlineSuccess, setOnlineSuccess] = useState(false);
    // payhide-7e4c1b05 — the fallback when an online payment can't start (never server text).
    const startFailedMessage = portalMode
        ? "We couldn't start this payment. Please try again, or contact the business to arrange payment."
        : "Couldn't start an online payment. Please try again.";

    // Reset state whenever the modal opens or the tab changes
    useEffect(() => {
        if (isOpen) {
            setActiveTab(portalMode ? 'online' : 'manual');
            setError(null);
            setPaymentData(null);
        }
    }, [isOpen, portalMode]);

    // Fetch a new PaymentIntent only when we actually need the online tab.
    // The cancellation guard prevents React StrictMode's double-invoke from
    // firing createPaymentIntent twice and creating orphaned PaymentIntents.
    useEffect(() => {
        if (!isOpen || activeTab !== 'online' || paymentData || !stripeKey) return;

        let cancelled = false;
        const createIntent = httpsCallable(functions, 'createPaymentIntent');

        createIntent({
            invoiceId: invoice.id,
            organizationId: invoice.organizationId
        })
        .then((result) => {
            if (cancelled) return;

            const { clientSecret, connectedAccountId } = result.data;

            if (!clientSecret) {
                setError(startFailedMessage);
                return;
            }

            // For destination charges connectedAccountId is null —
            // the PI lives on the platform so NO stripeAccount option is passed.
            // For direct charges it would be non-null and we'd scope Stripe to it.
            const stripePromise = (connectedAccountId)
                ? loadStripe(stripeKey, { stripeAccount: connectedAccountId })
                : loadStripe(stripeKey);

            setPaymentData({ clientSecret, stripePromise, connectedAccountId });
        })
        .catch((err) => {
            if (cancelled) return;
            console.error("createPaymentIntent error:", err);
            // SEAM 1b VERIFIER: pmodal-onboarding-error-f2a3b4c5
            // The server throws failed-precondition ("This organization is not
            // set up to receive payments yet.") when the org never finished
            // Stripe onboarding. Swap THAT one error for friendly customer copy;
            // every other error keeps the existing generic fallback.
            // payhide-7e4c1b05 — never render raw server text. Match on the message only:
            // the old `code === failed-precondition ||` also caught "This invoice is already
            // paid." and mislabelled it as not-set-up. Anything unrecognised gets the generic.
            const notOnboarded = /not set up to receive payments/i.test(err?.message || '');
            const alreadyPaid = /already paid/i.test(err?.message || '');
            // s0bcharge-bc5e9925 — S0b: the server refuses a card payment when the invoice's saved
            // balance is missing or doesn't match its payments (it flags the invoice for the office).
            const needsReview = /checked by the business/i.test(err?.message || '');
            if (notOnboarded) {
                setError("This business isn't set up to accept online payments yet. Please contact them to arrange payment for this invoice.");
            } else if (alreadyPaid) {
                setError(/or closed/i.test(err?.message || '') ? "This invoice is already paid or closed." : "This invoice is already paid.");
            } else if (needsReview) {
                setError(portalMode
                    ? "This invoice's balance needs to be checked by the business before it can be paid online. Please contact them to arrange payment."
                    : "Online card payment is paused for this invoice because its internal records don't add up. Contact Spatchy support to have it fixed.");
            } else {
                setError(startFailedMessage);
            }
        });

        // Cleanup: mark stale if React re-runs this effect (StrictMode)
        return () => { cancelled = true; };

    // startFailedMessage is a string (compared by value), so it only changes with portalMode.
    }, [isOpen, invoice, activeTab, paymentData, startFailedMessage, portalMode]);

    if (!isOpen) return null;

    // qbpaysync-6a1e7b52 -- computed once; suppressed in portal mode (the paying
    // customer cannot act on it). Passed to both payment forms.
    const syncWarning = portalMode ? null : getPaymentSyncWarning(invoice, quickbooksConnected);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
                    <h2 className="text-xl font-bold text-gray-800">
                        {portalMode ? 'Secure Payment' : 'Process Payment'}
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X size={24} />
                    </button>
                </div>

                {!portalMode && onlinePayAvailable && (
                    <div className="flex border-b bg-gray-50 sticky top-[60px] z-10">
                        <button
                            className={`flex-1 py-3 font-medium text-sm flex justify-center items-center gap-2 ${activeTab === 'manual' ? 'text-green-600 border-b-2 border-green-600 bg-white' : 'text-gray-500 hover:text-gray-700'}`}
                            onClick={() => setActiveTab('manual')}
                        >
                            <Banknote size={16} /> Record Manual
                        </button>
                        <button
                            className={`flex-1 py-3 font-medium text-sm flex justify-center items-center gap-2 ${activeTab === 'online' ? 'text-blue-600 border-b-2 border-blue-600 bg-white' : 'text-gray-500 hover:text-gray-700'}`}
                            onClick={() => setActiveTab('online')}
                        >
                            <CreditCard size={16} /> Pay Online
                        </button>
                    </div>
                )}

                <div className="p-6">
                    {activeTab === 'manual' ? (
                        <ManualPaymentForm
                            invoice={invoice}
                            currentUserProfile={currentUserProfile}
                            syncWarning={syncWarning}
                            onSuccess={() => { onClose(); window.location.reload(); }}
                        />
                    ) : (
                        <>
                            {!stripeKey ? (
                                <div className="text-center p-4 bg-yellow-50 rounded-lg text-yellow-800">
                                    <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-yellow-600" />
                                    <p className="font-bold">Payments Not Configured</p>
                                    <p className="text-sm mt-1">Please set <code>REACT_APP_STRIPE_PUBLISHABLE_KEY</code> in your .env file.</p>
                                </div>
                            ) : (
                                <>
                                    {error && (
                                        <div className="mb-4 bg-red-50 text-red-600 p-3 rounded-lg text-sm flex items-start gap-2">
                                            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                                            {error}
                                        </div>
                                    )}

                                    {paymentData ? (
                                        // ✅ key forces Elements to fully remount if the connected account changes
                                        <Elements
                                            key={paymentData.connectedAccountId || 'platform'}
                                            stripe={paymentData.stripePromise}
                                            options={{
                                                clientSecret: paymentData.clientSecret,
                                                appearance: { theme: 'stripe' }
                                            }}
                                        >
                                            <CheckoutForm
                                                invoice={invoice}
                                                syncWarning={syncWarning}
                                                connectedAccountId={paymentData.connectedAccountId}
                                                onSuccess={() => { setOnlineSuccess(true); setTimeout(() => { onClose(); window.location.reload(); }, 2000); }}
                                                onError={setError}
                                            />
                                        </Elements>
                                    ) : (
                                        !error && (
                                            <div className="flex justify-center p-8">
                                                <Loader2 className="animate-spin h-8 w-8 text-blue-600" />
                                            </div>
                                        )
                                    )}

                                    {onlineSuccess && (
                                        <div className="mt-4 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                                            <CheckCircle size={16} className="shrink-0" />
                                            Payment successful! Closing...
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PaymentModal;